import { LoaderCircle } from 'lucide-react';
import { forwardRef, type HTMLAttributes, type MouseEvent } from 'react';

import { cn } from '@heidi/utils';

import { CircularProgress } from '../circular-progress';
import { Skeleton } from '../skeleton';
import { FileCardDeleteButton } from './file-card-delete-button';

export interface FileCardLoadingProps extends HTMLAttributes<HTMLDivElement> {
  /** Size variant */
  size?: 'default' | 'mobile';
  /** Loading progress (0-100). Shows circular progress when provided, spinner otherwise. */
  loadingProgress?: number;
  /** Delete callback */
  onDelete?: (e: MouseEvent<HTMLButtonElement>) => void;

  /**
   * Accessible label for the delete control, named after the file it removes.
   * Supplied by `FileCard`, which holds the name this variant does not take.
   */
  deleteAriaLabel?: string;
}

/**
 * Loading variant of FileCard shown during file upload.
 * Displays a progress indicator (circular or spinner), skeleton text, and optional delete button.
 */
export const FileCardLoading = forwardRef<HTMLDivElement, FileCardLoadingProps>(
  (
    { size = 'default', loadingProgress, onDelete, deleteAriaLabel, className, ...props },
    ref
  ) => {
    const isMobileSize = size === 'mobile';
    const cardClasses = isMobileSize
      ? 'h-auto w-[204px] gap-2 rounded-xl py-1.5 ps-1.5 pe-2.5'
      : 'h-10 w-44 gap-2.5 rounded-lg py-1 ps-1 pe-2';
    const iconContainerClasses = isMobileSize
      ? 'size-10 rounded-lg'
      : 'size-8 rounded-md';
    const iconClasses = isMobileSize ? 'size-6' : 'size-4';
    const textContainerClasses = isMobileSize
      ? 'gap-2 justify-center pt-0'
      : 'gap-1 pt-0.5';
    const hasProgress = loadingProgress !== undefined;

    return (
      <div
        ref={ref}
        className={cn(
          'group relative flex items-center border border-outline bg-surface',
          cardClasses,
          className
        )}
        {...props}
      >
        <div
          className={cn(
            'flex aspect-square shrink-0 items-center justify-center overflow-hidden bg-background-tertiary',
            iconContainerClasses
          )}
        >
          {hasProgress ? (
            <CircularProgress progress={loadingProgress} size="sm" />
          ) : (
            <LoaderCircle
              className={cn('animate-spin text-text-secondary', iconClasses)}
            />
          )}
        </div>
        <div
          className={cn(
            'flex min-w-0 flex-[1_0_0] flex-col items-start overflow-hidden',
            textContainerClasses
          )}
        >
          <Skeleton className="h-3 w-full self-stretch rounded-lg bg-background-tertiary" />
          <Skeleton className="h-2 w-8 rounded-lg bg-background-tertiary" />
        </div>
        {onDelete && (
          <FileCardDeleteButton onDelete={onDelete} ariaLabel={deleteAriaLabel} />
        )}
      </div>
    );
  }
);

FileCardLoading.displayName = 'FileCardLoading';
